// A held session's register (no board draws one; the critic's missed 1, design 5.5): everyone whose
// access was open at the session, in its intake, and anyone already on it, each marked present,
// absent or excused, with a note only staff see. Owner, admin and support take any register, a host
// their own product's (hub.session_register reads it; the database stamps who marked each line and
// when, and refuses a session that hasn't started). Registers are ticked by hand: the hub never reads
// Zoom (Adam, 5 Oct, stage 6), so the board's "From Zoom" line has gone.
//
// The Accelerator critique (admin 3, 20 Oct): whoever isn't marked comes first, then by name, in the order the
// register opened in (a mark doesn't move anyone), and the picked mark is solid. On a mastermind's register, for
// owner and admin, each name opens that member in Masterminds and an absence takes the day it was caught up on
// (hub.record_catch_up, as the member's Sessions card writes it). Excused carries its hint, "agreed in advance".
import { useEffect, useId, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import { Button, Card, Crumbs, DateField, EmptyState, Loading, Options, Problem, adminPath, todayInUk, useLoad, ukDateTime } from '../ui/index.js'
import { shortDate } from '../../lib/format.js'
import { COPY } from './copy.js'
import { sessionState } from './model.js'
import { loadRegister, loadCatchUps } from './data.js'
// Mark all present, and a group's register counting towards the guarantee (the mastermind build)
import { MarkAllPresent } from '../mastermind/MarkAll.jsx'
import { MM, REGISTER } from '../mastermind/words.js'
import { recordCatchUp } from '../mastermind/data.js'

const STATUSES = ['present', 'absent', 'excused']
const OA = role => role === 'owner' || role === 'admin'
const dayOf = t => new Date(t).toLocaleDateString('en-CA', { timeZone: 'Europe/London' })

// Whoever isn't marked first, then by name: the order the register opens in
export function registerOrder(rows) {
  return [...(rows || [])].sort((a, b) => (a.status ? 1 : 0) - (b.status ? 1 : 0)
    || String(a.full_name || '').localeCompare(String(b.full_name || ''), 'en-GB')
    || String(a.person_id).localeCompare(String(b.person_id)))
}

export function Register({ sessionId, data, api, meName, role }) {
  const session = data.sessions.find(s => s.id === sessionId) || null
  const product = session ? data.products.find(p => p.id === session.product_id) : null
  const cohort = session ? data.cohorts.find(c => c.id === session.cohort_id) : null
  const module = session?.module_id ? data.modules.find(m => m.id === session.module_id) : null
  const started = session && sessionState(session, Date.now()) === 'held'
  const reg = useLoad(() => (started ? loadRegister(api, sessionId).then(registerOrder) : Promise.resolve([])), [sessionId, started])
  // a mastermind's register, for owner and admin: each name opens Masterminds, and an absence takes its caught-up day
  const staffMm = OA(role) && product?.type === 'mastermind'
  const catchUps = useLoad(() => (started && staffMm ? loadCatchUps(api, sessionId).catch(() => ({})) : Promise.resolve({})), [sessionId, started, staffMm])

  const label = product?.type === 'mastermind' ? COPY.session : (module?.label || session?.title || '')
  const date = session?.starts_at ? shortDate(session.starts_at) : ''
  const crumbs = <Crumbs items={[{ label: COPY.registerCrumb, to: adminPath.dates() }, { label: session ? `${label}, ${date}` : COPY.registerLabel }]} />

  if (!session || !product) {
    return <>{crumbs}<EmptyState>{COPY.registerGone}</EmptyState></>
  }
  const aside = [product.name, cohort?.name].filter(Boolean).join(', ')
  let body
  if (!started) body = <EmptyState>{COPY.registerNotStarted}</EmptyState>
  else if (reg.status === 'loading') body = <Loading />
  else if (reg.status === 'error') body = <div className="adc-pad"><Problem error={reg.error} onRetry={reg.reload} /></div>
  else if (!reg.data?.length) body = <EmptyState>{COPY.registerEmpty}</EmptyState>
  else {
    const rows = reg.data
    const marked = rows.filter(r => r.status).length
    body = (
      <>
        <p className="adc-reg-lede">{product.type === 'mastermind' ? REGISTER.groupLede : COPY.registerLede} <span className="adc-reg-count">{COPY.marked(marked, rows.length)}</span></p>
        <MarkAllPresent sessionId={session.id} rows={rows} api={api} meName={meName}
          onSaved={lines => reg.setData(list => list.map(x => { const l = lines.find(y => y.person_id === x.person_id); return l ? { ...x, ...l, recorded_by_name: meName || '' } : x }))} />
        <ul className="adc-reg" aria-label={COPY.registerLabel}>
          {rows.map(r => (
            <RegisterLine key={r.person_id} row={r} session={session} api={api} meName={meName} group={cohort?.kind === 'group'}
              member={staffMm ? adminPath.mmMember(product.slug, r.person_id) : null}
              caughtUp={staffMm ? (catchUps.data?.[r.person_id] || null) : undefined}
              onCaughtUp={on => catchUps.setData(m => ({ ...(m || {}), [r.person_id]: on }))}
              onSaved={line => {
                reg.setData(list => list.map(x => (x.person_id === line.person_id ? { ...x, ...line } : x)))
                // any mark but absent clears the caught-up day (the database's guard_catch_up)
                if (line.status !== 'absent') catchUps.setData(m => ({ ...(m || {}), [line.person_id]: null }))
              }} />
          ))}
        </ul>
      </>
    )
  }
  return (
    <>
      {crumbs}
      <Card title={COPY.registerTitle(label, date)} aside={aside} body={false} className="adc-reg-card">
        {body}
      </Card>
    </>
  )
}

function RegisterLine({ row, session, api, onSaved, meName, group, member, caughtUp, onCaughtUp }) {
  const [note, setNote] = useState(row.note || '')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const ids = useId()

  async function mark(status, nextNote = row.note) {
    if (busy) return
    setBusy(true)
    setProblem(null)
    try {
      const line = await api.register_line({ session_id: session.id, person_id: row.person_id, status, note: nextNote?.trim() ? nextNote.trim() : null })
      onSaved({ ...line, recorded_by_name: meName || '' })
    } catch (e) {
      setProblem(api.words(e))
    } finally {
      setBusy(false)
    }
  }

  // a line marked outside the hub (the Accelerator's copied registers) has no marker's name: when only
  const by = !(row.status && row.recorded_at) ? COPY.notMarked
    : row.recorded_by_name ? COPY.markedBy(row.recorded_by_name, ukDateTime(row.recorded_at)) : COPY.markedWhen(ukDateTime(row.recorded_at))
  return (
    <li className="adc-reg-line">
      <span className="adc-reg-who">
        {member
          ? <Link to={member} className="adc-reg-name adc-reg-name--link" id={`${ids}-n`}>{row.full_name}</Link>
          : <span className="adc-reg-name" id={`${ids}-n`}>{row.full_name}</span>}
        <span className="adc-reg-email">{row.email}</span>
        <span className="adc-reg-tags">
          {!row.expected && <span className="ad-tag">{group ? COPY.notExpectedGroup : COPY.notExpected}</span>}
          {row.is_test && <span className="ad-tag">{COPY.testLogin}</span>}
        </span>
      </span>
      <span className="adc-reg-mark">
        <Options row labelledBy={`${ids}-n`} value={row.status || null} disabled={busy}
          options={STATUSES.map(s => ({ value: s, label: COPY.statuses[s], note: COPY.statusHints[s] }))} onChange={s => mark(s, note)} />
        <span className="adc-reg-by">{by}</span>
      </span>
      <span className="adc-reg-note">
        <input className="ad-input" aria-label={COPY.noteAria(row.full_name)} placeholder={COPY.notePlaceholder} value={note}
          onChange={e => { setNote(e.target.value); setProblem(null) }}
          onBlur={() => {
            if ((note.trim() || null) === (row.note || null)) return
            if (!row.status) { setProblem(COPY.noteNeedsMark); return }
            mark(row.status, note)
          }} />
      </span>
      {caughtUp !== undefined && row.status === 'absent' && (
        <CatchUp row={row} session={session} api={api} saved={caughtUp} onSaved={onCaughtUp} />
      )}
      {problem && <span className="ad-error adc-reg-problem" role="alert">{problem}</span>}
    </li>
  )
}

// "Caught up on" on an absence (owner, admin, a mastermind's register): the day they watched the recording and
// sent Adam their update, from the session's day to today, through hub.record_catch_up; Clear takes it away
function CatchUp({ row, session, api, saved, onSaved }) {
  const [on, setOn] = useState(saved || '')
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  // the day as read, once the register's caught-up days are in
  useEffect(() => { setOn(saved || '') }, [saved])
  const day = shortDate(session.starts_at)
  async function save(value) {
    if (busy) return
    setBusy(true)
    setLine(null)
    try {
      const answer = await recordCatchUp(api, { session: session.id, person: row.person_id, on: value || null }, { dateLine: MM.caughtBadDate })
      onSaved(answer?.caught_up_on ?? (value || null))
      setLine({ text: !value ? MM.caughtCleared : answer?.in_time ? MM.caughtInTime(day) : MM.caughtLate(answer?.days_after ?? '') })
    } catch (e) {
      setLine({ bad: true, text: e.message })
    } finally {
      setBusy(false)
    }
  }
  return (
    <span className="adc-reg-catch">
      <DateField label={MM.caughtUpOn} value={on} onChange={v => { setOn(v); setLine(null) }} min={dayOf(session.starts_at)} max={todayInUk()} plain />
      <span className="adc-reg-catch-acts">
        <Button size="xs" kind="ink" busy={busy} disabled={!on || on === saved} onClick={() => save(on)}>{MM.caughtSave}</Button>
        {saved && <Button size="xs" disabled={busy} onClick={() => { setOn(''); save(null) }}>{MM.caughtClear}</Button>}
      </span>
      {line && <span className={line.bad ? 'ad-error' : 'adc-flash'} role={line.bad ? 'alert' : 'status'}>{line.text}</span>}
    </span>
  )
}
